import { useEffect, useRef, useState } from 'react';
import { sendContactMessage, fetchContactChallenge, ApiError } from '../api.ts';
import { solveChallenge } from '../pow.ts';

/**
 * Contact us.
 *
 * Henry Liner, 2026-09-17: "please Add contact us tab w name, phone, email, and description
 * box. Have inquiries forward to me please". The four boxes are his, in his order. The
 * message is stored and then emailed to him - see routes/contact.ts - so "sent" on this
 * screen means the server has it, not merely that the request left the browser.
 *
 * Public: a broker thinking about signing up is exactly who writes to a contact page.
 *
 * The page does not name who reads the messages. The follow-up chat stopped naming Henry for
 * a reason (guardrails.ts), and a contact form is the easiest place on the site to fish for it.
 */

/** A solved challenge, and when its answer arrived here - the clock the minimum wait runs on. */
interface Proof {
  readonly salt: string; readonly challenge: string; readonly signature: string;
  readonly number: number; readonly receivedAt: number;
}

/**
 * The server refuses a challenge younger than three seconds. Measured from when THIS browser
 * received it, which is never earlier than the server issued it - so the wait holds whatever
 * either clock says. The extra 200ms is for the request on its way back.
 */
const MIN_WAIT_MS = 3_200;

async function prepareProof(signal?: AbortSignal): Promise<Proof> {
  const c = await fetchContactChallenge();
  const receivedAt = Date.now();
  const number = await solveChallenge(c, signal);
  if (number === null) throw new Error(signal?.aborted ? 'aborted' : 'challenge could not be solved');
  return { salt: c.salt, challenge: c.challenge, signature: c.signature, number, receivedAt };
}

const sleep = (ms: number) => new Promise<void>((r) => { setTimeout(r, ms); });

function contactError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.detail) return err.detail;
    if (err.code === 'invalid_request') {
      return 'Check the form: every box is needed, the email has to be a real address, and '
        + 'the phone number needs at least seven digits.';
    }
    return `Your message could not be sent (${err.status}). Please try again.`;
  }
  if (err instanceof TypeError) {
    return 'Cannot reach the server. It may be restarting - try again in a few seconds.';
  }
  return 'Your message could not be sent. Please try again.';
}

export default function ContactUs({ onClose, minWaitMs = MIN_WAIT_MS }: {
  onClose: () => void;
  /** Tests pass 0. */
  minWaitMs?: number;
}) {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  // The honeypot. A person never sees or reaches it, so it stays empty for them.
  const [website, setWebsite] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);

  // The proof-of-work starts the moment the page opens, so it is solved while the broker is
  // still typing and Send is instant. Held as a promise: pressing Send early simply waits on it.
  const proof = useRef<Promise<Proof> | null>(null);
  const abort = useRef<AbortController | null>(null);
  const mounted = useRef(false);
  const startProof = (): Promise<Proof> => {
    // After the page is gone a new puzzle would run to the end with nothing left to abort it
    // - tens of seconds of a phone's CPU for a message nobody will send (review finding).
    if (!mounted.current) {
      const gone = Promise.reject(new Error('aborted'));
      gone.catch(() => {}); // called as a statement from the retry path; nobody awaits it
      return gone;
    }
    abort.current?.abort();
    const ctl = new AbortController();
    abort.current = ctl;
    const p = prepareProof(ctl.signal);
    p.catch(() => {}); // surfaced when Send awaits it, not as an unhandled rejection meanwhile
    proof.current = p;
    return p;
  };
  useEffect(() => {
    mounted.current = true;
    startProof();
    return () => { mounted.current = false; abort.current?.abort(); };
  }, []);

  const card = { maxWidth: 560, margin: '0 auto' } as const;
  const ready = [name, phone, email, message].every((v) => v.trim() !== '');

  async function submit(): Promise<void> {
    setBusy(true); setError('');
    try {
      // One automatic retry with a fresh challenge. A challenge can be spent (the server kept
      // an earlier send whose reply never arrived) or have aged out while a long message was
      // written; neither is the broker's to fix, so they never see it unless it happens twice.
      for (let attempt = 0; ; attempt++) {
        let p: Proof;
        try {
          p = await (proof.current ?? startProof());
        } catch (err) {
          // A challenge that could not be fetched or solved - start over, so pressing Send
          // again tries a fresh one rather than re-reading the same failure.
          startProof();
          throw err;
        }
        const wait = p.receivedAt + minWaitMs - Date.now();
        if (wait > 0) await sleep(wait);
        try {
          await sendContactMessage({
            name: name.trim(), phone: phone.trim(), email: email.trim(), message: message.trim(),
            website,
            pow: { salt: p.salt, challenge: p.challenge, signature: p.signature, number: p.number },
          });
          setSent(true);
          return;
        } catch (err) {
          const stale = err instanceof ApiError
            && (err.code === 'challenge_failed' || err.code === 'challenge_used');
          if (stale) startProof();
          if (!stale || attempt >= 1) throw err;
        }
      }
    } catch (err) {
      setError(contactError(err));
    } finally {
      setBusy(false);
    }
  }

  if (sent) {
    return (
      <div className="card" role="status" style={{ ...card, textAlign: 'center', padding: '36px 20px' }}>
        <h2 style={{ margin: '0 0 8px', fontSize: 24 }}>Thanks, {name.trim().split(/\s+/)[0]}</h2>
        <p style={{ margin: '0 0 20px', color: 'var(--muted)', fontSize: 14, lineHeight: 1.6 }}>
          We have your message and will get back to you at {email.trim()} or {phone.trim()}.
        </p>
        <button type="button" className="btn btn-primary" onClick={onClose}>Back to search</button>
      </div>
    );
  }

  return (
    <form className="card" style={card}
          onSubmit={(e) => { e.preventDefault(); if (ready && !busy) void submit(); }}>
      <h2 style={{ margin: '0 0 6px', fontSize: 24 }}>Contact us</h2>
      <p style={{ margin: '0 0 20px', color: 'var(--muted)', fontSize: 14, lineHeight: 1.6 }}>
        Questions about a file, a lender, or LenderHound? Send us a note and we will get back
        to you.
      </p>

      <label className="lbl" htmlFor="contact-name">Name</label>
      <input id="contact-name" className="finput" autoComplete="name" autoFocus
             maxLength={200} value={name} onChange={(e) => setName(e.target.value)} />

      <label className="lbl" htmlFor="contact-phone" style={{ marginTop: 14 }}>Phone</label>
      <input id="contact-phone" className="finput" type="tel" autoComplete="tel"
             placeholder="(310) 555-0100" maxLength={40}
             value={phone} onChange={(e) => setPhone(e.target.value)} />

      <label className="lbl" htmlFor="contact-email" style={{ marginTop: 14 }}>Email</label>
      <input id="contact-email" className="finput" type="email" autoComplete="email"
             maxLength={320} value={email} onChange={(e) => setEmail(e.target.value)} />

      <label className="lbl" htmlFor="contact-message" style={{ marginTop: 14 }}>Message</label>
      <textarea id="contact-message" className="ftarea" rows={6} maxLength={5000}
                value={message} onChange={(e) => setMessage(e.target.value)}
                style={{ width: '100%', resize: 'vertical' }} />

      {/* The honeypot. Off-screen rather than display:none, which some bots check for; out of
          the tab order and hidden from assistive tech so no person ever lands in it. Labelled
          and named like nothing a password manager or address autofill would recognise - a
          field it filled would silently drop a real person's message. */}
      <div aria-hidden="true" style={{ position: 'absolute', left: -10000, width: 1, height: 1, overflow: 'hidden' }}>
        <label htmlFor="contact-hp">Leave this field empty</label>
        <input id="contact-hp" name="lh-contact-hp" type="text" tabIndex={-1} autoComplete="off"
               data-lpignore="true" data-1p-ignore="true"
               value={website} onChange={(e) => setWebsite(e.target.value)} />
      </div>

      {error ? <div className="err-text" role="alert" style={{ marginTop: 10 }}>{error}</div> : null}

      <button type="submit" className="btn btn-primary" style={{ width: '100%', marginTop: 18 }}
              disabled={busy || !ready}>
        {busy ? <><span className="spin" /> &nbsp;Sending…</> : 'Send message'}
      </button>
    </form>
  );
}
